import { FormItem, TextInputExtended } from '@cw-ui/form-components';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import { ACTIVE_SCREEN_KEY, formatMessage } from 'cw-ui-core';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import DropdownField from 'src/asio-neon-components/DropdownField/DropdownField';
import TextAreaField from 'src/asio-neon-components/TextAreaField';
import TextInputField from 'src/asio-neon-components/TextInputField';
import InputFieldContainer from 'src/components/InputField/InputFieldContainer';
import { PERMISSIONS } from 'src/constants';
import { getTestConfigurationStatus } from 'src/features/authentication-v2/components/utils';
import { EDIT_EXTERNAL_IDP, YOUR_EXISTING_SECRET } from 'src/features/authentication-v2/constants';
import { useStylesHook } from 'src/hooks/utilHooks';
import Locale from 'src/i18n';
import { checkIfUserHaveGivenPermissions } from 'src/util/Util';
import getStyles from './ConfigureExternalIdPStyles';

const CopyToClipboard = ({ value, onCopy }) => {
    const handleCopyToClipboard = () => {
        onCopy(value);
    };

    return (
        <IconButton onClick={handleCopyToClipboard} edge="end">
            <ContentCopy />
        </IconButton>
    );
};

const ConfigureExternalIdP = props => {
    const {
        onCopy,
        onTestConfiguration,
        validateField,
        formErrors,
        onDidMount,
        scheme,
        isValidAuthorityUrl,
        resetConfigurationValidity,
        identityProviderExtraFields,
        readOnly = false,
        screenKey = ACTIVE_SCREEN_KEY,
        hasSecrets,
        onUpdateClientSecret,
    } = props;
    const styles = useStylesHook(getStyles);

    const initialValueForClientSecret = hasSecrets ? YOUR_EXISTING_SECRET : '';
    const [clientSecretInput, setClientSecretInput] = useState(initialValueForClientSecret);
    const handleClientSecretChange = e => {
        setClientSecretInput(e.target.value);
        onUpdateClientSecret(e.target.value);
    };

    useEffect(() => {
        onDidMount();
        onUpdateClientSecret(initialValueForClientSecret);
        setClientSecretInput(initialValueForClientSecret);
    }, [scheme]);

    const testConfig = getTestConfigurationStatus(isValidAuthorityUrl);

    const onAfterAuthorityUrlChange = () => {
        validateField('authority');
        resetConfigurationValidity();
    };
    const hasRequiredPermissions = checkIfUserHaveGivenPermissions([PERMISSIONS.AUTHENTICATION_UPDATE]);
    return (
        <Paper sx={styles.formContainer}>
            <Typography variant="h6" sx={styles.formTitle}>
                {formatMessage(Locale.externalIdPTitle)}
            </Typography>
            <Stack
                sx={{ overflowY: 'auto', height: '40vh', px: '3px', width: '100%', flexDirection: 'row', flexGrow: 1 }}>
                <Stack flexGrow={1} height="100%" sx={styles.firstFormBlock}>
                    <InputFieldContainer
                        onAfterChange={() => validateField('clientName')}
                        path={['identityProvider', 'clientName']}
                        screenKey={screenKey}>
                        <TextInputField
                            disabled={readOnly}
                            error={formErrors?.clientName}
                            validationError={formErrors?.clientName}
                            helperText={formErrors?.clientName}
                            label={formatMessage(Locale.displayName)}
                            placeholder={formatMessage(Locale.createAppRoleRoleNamePlaceholder)}
                            size="medium"
                            id="clientName"
                            required
                            onBlur={() => validateField('clientName')}
                        />
                    </InputFieldContainer>
                    <InputFieldContainer
                        onAfterChange={() => validateField('clientId')}
                        path={['identityProvider', 'clientId']}
                        screenKey={screenKey}>
                        <TextInputField
                            disabled={screenKey === EDIT_EXTERNAL_IDP}
                            error={formErrors?.clientId}
                            validationError={formErrors?.clientId}
                            helperText={formErrors?.clientId}
                            label={formatMessage(Locale.clientId)}
                            placeholder={formatMessage(Locale.createAppRoleRoleNamePlaceholder)}
                            size="medium"
                            id="clientId"
                            required
                            onBlur={() => validateField('clientId')}
                        />
                    </InputFieldContainer>
                    <InputFieldContainer
                        onAfterChange={onAfterAuthorityUrlChange}
                        path={['identityProvider', 'authority']}
                        screenKey={screenKey}>
                        <TextInputField
                            disabled={screenKey === EDIT_EXTERNAL_IDP}
                            error={formErrors?.authority || (_.isBoolean(isValidAuthorityUrl) && !isValidAuthorityUrl)}
                            validationError={formErrors?.authority}
                            helperText={formErrors?.authority}
                            label={formatMessage(Locale.authenticationAddProviderDialogLabelAuthorityUrl)}
                            placeholder={formatMessage(Locale.createAppRoleRoleNamePlaceholder)}
                            size="medium"
                            id="authorityUrl"
                            required
                            onBlur={() => validateField('authority')}
                        />
                    </InputFieldContainer>
                    <Stack sx={{ width: '100%' }} flexDirection="row-reverse">
                        {hasRequiredPermissions && (
                            <Button
                                sx={styles.testConfiguration(testConfig.status)}
                                mb="4px"
                                onClick={onTestConfiguration}
                                disabled={readOnly}>
                                {testConfig.icon}
                                {testConfig.label}
                            </Button>
                        )}
                    </Stack>
                    <FormItem
                        className="textInputFormControl"
                        id="id"
                        labelProps={{
                            disabled: readOnly,
                            error: false,
                            required: false,
                        }}
                        labelText={formatMessage(Locale.clientSecret)}
                        shrink
                        size="medium">
                        <TextInputExtended
                            id="clientSecretInput"
                            onChange={e => {
                                handleClientSecretChange(e);
                            }}
                            placeholder={formatMessage(Locale.clientSecretsPlaceHolder)}
                            type="password"
                            value={clientSecretInput}
                        />
                    </FormItem>
                </Stack>
                <Stack flexGrow={1} sx={styles.secondFormBlock}>
                    <Box sx={{ pb: '12px' }}>
                        <InputFieldContainer path={['identityProvider', 'description']} screenKey={screenKey}>
                            <TextAreaField
                                disabled={readOnly}
                                maxCharsAllowed={100}
                                label={formatMessage(Locale.description)}
                                placeholder={formatMessage(Locale.createAppRoleRoleNamePlaceholder)}
                                size="small"
                                counter={{ displayLength: true, displayMaxLength: true }}
                                id="description"
                                scrollable
                                rows={3}
                            />
                        </InputFieldContainer>
                    </Box>
                    <Box sx={{ pb: '12px' }}>
                        <InputFieldContainer
                            onAfterChange={() => validateField('responseType')}
                            path={['identityProvider', 'responseType']}
                            screenKey={screenKey}>
                            <DropdownField
                                disabled={readOnly}
                                error={formErrors?.responseType}
                                validationError={formErrors?.responseType}
                                description={formErrors?.responseType}
                                options={['code', 'id_token', 'token', 'id_token token', 'code id_token token']}
                                path={['responseType']}
                                label={formatMessage(Locale.authenticationAddProviderDialogLabelResponseType)}
                                placeholder={formatMessage(Locale.chooseAnOption)}
                                size="medium"
                                required
                                onBlur={() => validateField('responseType')}
                            />
                        </InputFieldContainer>
                    </Box>

                    <InputFieldContainer path={['identityProviderExtraFields', 'redirectUri']} screenKey={screenKey}>
                        <TextInputField
                            disabled
                            label={formatMessage(Locale.authenticationAddProviderDialogLabelRedirectUri)}
                            placeholder={formatMessage(Locale.createAppRoleRoleNamePlaceholder)}
                            size="medium"
                            id="redirectUri"
                            adornmentComponent={
                                <CopyToClipboard value={identityProviderExtraFields?.redirectUri} onCopy={onCopy} />
                            }
                        />
                    </InputFieldContainer>
                    <InputFieldContainer path={['identityProviderExtraFields', 'logoutUri']} screenKey={screenKey}>
                        <TextInputField
                            disabled
                            label={formatMessage(Locale.authenticationAddProviderDialogLabelSignouttUri)}
                            placeholder={formatMessage(Locale.createAppRoleRoleNamePlaceholder)}
                            size="medium"
                            id="logoutUri"
                            adornmentComponent={
                                <CopyToClipboard value={identityProviderExtraFields?.logoutUri} onCopy={onCopy} />
                            }
                        />
                    </InputFieldContainer>
                </Stack>
            </Stack>
        </Paper>
    );
};

export default ConfigureExternalIdP;
